/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GenericCard from '@ui-components/Card/GenericCard';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericTabs, { TabOption } from '@ui-components/Tabs/GenericTabs';
import CopyableValue from '@shared/components/CopyableValue';
import { useDetailTabParam } from '@shared/navigation/tabs/useDetailTabParam';
import { ReactComponent as IconEdit } from '../../../../../shared/assets/Icons/IconEdit_KE.svg';
import { ReactComponent as IconCheck } from '../../../../../shared/assets/Icons/IconCheckSquared.svg';
import { ReactComponent as IconCancel } from '../../../../../shared/assets/Icons/IconCrossSquare.svg';
import GenericButton from '@ui-components/Button/GenericButton';
import { ReactComponent as IconRefresh } from '../../../../../shared/assets/Icons/IconRefreshV2.svg';
import { ReactComponent as IconTrash } from '../../../../../shared/assets/Icons/IconDelete_KE.svg';
import { GenericTable } from '@ui-components/Table/GenericTable';
import ClusterIAMPermissionsModal from './ClusterIAMPermissionsModal';
import GenericModal from '@ui-components/Modal/GenericModal';
import GenericRadioGroup from '@ui-components/RadioGroup/GenericRadioGroup';
import GenericAlert from '@ui-components/Alert/GenericAlert';
import ReflectorsAssociated from './ClusterReflector/ReflectorsAssociated';
import BgpDetailsPage from '../../BgpDetails/BgpDetailsPage';
import CloudPropagationPage from '../../CloudPropagation/CloudPropagationPage';
import { isAnyVersionBelow, NEXTHOP_RETRY_MIN_VERSION, NEXTHOP_WATCH_MIN_VERSION } from '../../../../helpers/cloudPropagationHelpers';
import CloudElementsPage, { CloudElementsPageProps } from '../../CloudElements/CloudElementsPage';
import { CLOUD_ELEMENTS_SUGGESTED_KEYS_CLUSTER } from '../../CloudElements/CloudElementsData';
import SyslogPage from '../../Syslog/SyslogPage';
import { clusterCloudPropagationColumns } from './ClusterCloudPropagation/ClusterCPData';
import { ClusterIAMResponse } from './ClusterIAMPermissionsData';
import { getClusterById, updateClusterName, updateClusterTags, getClusterIamPermissions, dissolveCluster, getBgpConfig } from '../request/request';
import { getCloudPropagationRulesByCluster, type ReflectorBgpConfig } from '../request/request';
import { AxiosError } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from '@redux/hooks';
import LicenseGate from '../../../shared/LicenseGate';
import { useLicenseGate } from '../../../shared/useLicenseGate';
import { ClusterDetailItem, getClusterDetailsSection, clusterCloudElementsColumns } from './ClusterDetailsData';
import { transformClusterIamPermissions } from '../../../../helpers/formattersShapedData';
import { format } from 'date-fns';
import { DataStateView, ErrorStateView } from 'kontrolEdge/components/Backbone/BackboneDetails/StateViews';
import { TableSkeleton, SkeletonLine } from '@ui-components/Skeleton/GenericSkeleton';
import { EditableTagList } from 'kontrolEdge/components/Shared/NameTagEdit/EditableTagList';
import { apiTagsToSummaries } from 'kontrolEdge/components/Shared/NameTagEdit/useResourceTags';
import { dropCachedFetch, readCachedFetch, useCachedFetch, writeCachedFetch } from '@hooks/useCachedFetch';
import { isTenantScopeForbidden, TENANT_OUT_OF_SCOPE_MESSAGE } from '@shared/helpers/tenantScope';
import {
  clusterBgpConfigCacheKey,
  clusterCloudPropagationCacheKey,
  clusterDetailsCacheKey,
  clusterIamCacheKey,
  clusterListCacheKey,
} from '../../../../helpers/cacheKeys';

// Define the interface for cluster details


const TABS: TabOption[] = [
  { label: 'Details', value: 'Details' },
  { label: 'BGP', value: 'BGP' },
  { label: 'Cloud Propagation', value: 'Cloud Propagation' },
  { label: 'Cloud Elements', value: 'Cloud Elements' },
  { label: 'Syslog', value: 'Syslog' },
];
const TAB_VALUES = TABS.map((tab) => tab.value);

function getIamTagState(iamData: ClusterIAMResponse | null) {
  if (!iamData || !iamData.permissions || !Array.isArray(iamData.permissions) || iamData.permissions.length === 0) {
    return { label: '-', variant: 'neutral' };
  }
  
  // Check if both reflectors have all permissions allowed
  const allAllowed = iamData.permissions.every(p => p.reflector1Enabled && p.reflector2Enabled);
  // Check if both reflectors have all permissions denied
  const allDenied = iamData.permissions.every(p => !p.reflector1Enabled && !p.reflector2Enabled);
  
  if (allAllowed) return { label: 'Allowed', variant: 'success' };
  if (allDenied) return { label: 'Denied', variant: 'error' };
  return { label: 'Degraded', variant: 'warning' };
}

/** GET clusters/:id/bgp-config, as far as this page reads it. */
type ClusterBgpConfig = ReflectorBgpConfig & { reflectorInstance?: { instanceLimits?: unknown } };

/** The cluster as the page renders it: instance timestamps already formatted. */
function formatCluster<T extends { instances?: Array<{ lastUpdate?: string }> }>(raw: T) {
  return {
    ...raw,
    instances: Array.isArray(raw.instances)
      ? raw.instances.map((inst) => ({
          ...inst,
          lastUpdate: inst.lastUpdate ? format(new Date(inst.lastUpdate), 'M/d/yyyy, h:mm:ss a') : '-',
        }))
      : [],
  };
}

function ClusterDetailsPage() {
  const [activeTab, setActiveTab] = useDetailTabParam(TAB_VALUES, 'Details');
  const [isEditing, setIsEditing] = useState<{ [key: string]: boolean }>({});
  const [fieldValues, setFieldValues] = useState<{ [key: string]: string }>({});
  const [isIamModalOpen, setIsIamModalOpen] = useState(false);
  const [isDissolveModalOpen, setIsDissolveModalOpen] = useState(false);
  const [dissolveOption, setDissolveOption] = useState<string>('keep');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [dissolveLoading, setDissolveLoading] = useState(false);
  const [routerId2, setRouterId2] = useState('');

  const { id: clusterId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;
  const tenantId = useAppSelector((state: any) => state.selection?.tenantId);

  // The last answers for this cluster paint at once when the user comes back (a view
  // switch, Back); the requests below still run and replace them.
  const clusterCacheKey = clusterId && tenantId ? clusterDetailsCacheKey(clusterId, tenantId) : null;
  const cloudPropagationCacheKey = clusterId && tenantId ? clusterCloudPropagationCacheKey(clusterId, tenantId) : null;
  const cachedCluster = readCachedFetch(clusterCacheKey);
  const [cluster, setCluster] = useState<any>(() => cachedCluster ?? null);
  // Only a first visit waits on the skeleton.
  const [clusterLoading, setClusterLoading] = useState(() => Boolean(clusterCacheKey) && !cachedCluster);
  const [clusterError, setClusterError] = useState<string | null>(null);
  const [cloudPropagationRules, setCloudPropagationRules] = useState<any[]>(
    () => readCachedFetch(cloudPropagationCacheKey) ?? []
  );
  const [cloudPropagationLoading, setCloudPropagationLoading] = useState(false);
  const [cloudPropagationError, setCloudPropagationError] = useState<string | null>(null);
  // The route id can change under a mounted page (switching between two cluster views):
  // show that cluster's cached answers or the skeleton, never the previous one's.
  const [shownClusterKey, setShownClusterKey] = useState(clusterCacheKey);
  if (shownClusterKey !== clusterCacheKey) {
    setShownClusterKey(clusterCacheKey);
    setCluster(cachedCluster ?? null);
    setClusterLoading(Boolean(clusterCacheKey) && !cachedCluster);
    setClusterError(null);
    setCloudPropagationRules(readCachedFetch(cloudPropagationCacheKey) ?? []);
    setCloudPropagationError(null);
  }
  // Late answers for a cluster no longer on screen still fill the cache, not the page.
  const clusterKeyRef = useRef(clusterCacheKey);
  clusterKeyRef.current = clusterCacheKey;
  const cloudPropagationKeyRef = useRef(cloudPropagationCacheKey);
  cloudPropagationKeyRef.current = cloudPropagationCacheKey;
  const showFetchedCluster = useCallback((key: string | null, next: unknown) => {
    writeCachedFetch(key, next);
    if (clusterKeyRef.current === key) setCluster(next);
  }, []);
  // A tenant outside the caller's scope (CTX-866): forget what was cached for this cluster
  // and show the refusal, not its last known state.
  const bgpConfigCacheKey = clusterId && tenantId ? clusterBgpConfigCacheKey(clusterId, tenantId) : null;
  const showTenantRefusal = useCallback((key: string | null) => {
    dropCachedFetch(key);
    dropCachedFetch(bgpConfigCacheKey);
    if (clusterKeyRef.current !== key) return;
    setCluster(null);
    setClusterError(TENANT_OUT_OF_SCOPE_MESSAGE);
  }, [bgpConfigCacheKey]);

  // A propagation rule's BGP neighbors can't be mapped back to a specific instance
  // in the frontend, so every version gate in the CPR modal is evaluated against
  // the whole cluster (CTX-163, CTX-181).
  const clusterInstanceVersions: Array<string | undefined> = useMemo(
    () => (cluster?.instances ?? []).map((instance: { version?: string }) => instance?.version),
    [cluster]
  );

  // Details tab: first load and refresh both use skeletons (no spinner overlay)
  const clusterDetailsHasStartedLoadRef = useRef(false);
  const clusterDetailsHasCompletedFirstLoadRef = useRef(false);
  if (clusterLoading) clusterDetailsHasStartedLoadRef.current = true;
  if (!clusterLoading && clusterDetailsHasStartedLoadRef.current) clusterDetailsHasCompletedFirstLoadRef.current = true;
  const isClusterDetailsFirstLoad = clusterLoading && !clusterDetailsHasCompletedFirstLoadRef.current;

  // One request covers every member: the cluster endpoint resolves all of them, and a cluster
  // is gated when ANY member's license is invalid (CTX-346). Keyed on the cluster id only, so
  // switching tabs does not re-request.
  const licenseGate = useLicenseGate({ scope: 'cluster', id: clusterId });
  const inFlightClusterKeyRef = useRef<string | null>(null);

  /**
   * An explicit refresh (Sync, retry, tags or a role change) keeps its skeleton: the user
   * is waiting for the result of what they did, not for the cached state.
   * @param skipLoadingState When true, caller owns loading state (e.g. handleRefreshDetails). Prevents double loader.
   */
  const refreshClusterDetails = React.useCallback(async (skipLoadingState?: boolean) => {
    if (!clusterId) return;

    if (!skipLoadingState) {
      setClusterLoading(true);
      setClusterError(null);
    }
    const requestKey = clusterCacheKey;
    try {
      if (!tenantId) return;
      const token = await getAccessTokenSilentlyRef.current();
      const res = await getClusterById(token, clusterId, tenantId);
      showFetchedCluster(requestKey, formatCluster(res.data));
    } catch (err) {
      if (isTenantScopeForbidden(err)) showTenantRefusal(requestKey);
      else if (clusterKeyRef.current === requestKey) setClusterError('Failed to load cluster details');
    } finally {
      if (!skipLoadingState && clusterKeyRef.current === requestKey) setClusterLoading(false);
    }
  }, [clusterId, tenantId, clusterCacheKey, showFetchedCluster, showTenantRefusal]);

  // IAM permissions for the cluster, cached like the details they sit in.
  const {
    data: iamData = null,
    error: iamPermissionsError,
    isLoading: iamPermissionsLoading,
    reload: reloadIamPermissions,
  } = useCachedFetch<ClusterIAMResponse>(clusterId && tenantId ? clusterIamCacheKey(clusterId, tenantId) : null, async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await getClusterIamPermissions(token, Number(clusterId), tenantId);
      // Use centralized transformer
      return transformClusterIamPermissions(res);
    } catch (err) {
      // A tenant-scope refusal goes up as is: the hook drops the cached permissions.
      if (isTenantScopeForbidden(err)) throw err;
      console.error('Error fetching cluster IAM permissions:', err);
      throw new Error('Failed to load IAM permissions');
    }
  });

  // Fetch on arrival (one in flight per cluster and tenant); a cached answer stays up meanwhile.
  useEffect(() => {
    if (!clusterId || !tenantId || !clusterCacheKey) return;
    if (inFlightClusterKeyRef.current === clusterCacheKey) return;
    inFlightClusterKeyRef.current = clusterCacheKey;
    const requestKey = clusterCacheKey;
    if (!readCachedFetch(requestKey)) setClusterLoading(true);
    setClusterError(null);
    const fetchCluster = async () => {
      try {
        const token = await getAccessTokenSilentlyRef.current();
        const res = await getClusterById(token, clusterId, tenantId);
        showFetchedCluster(requestKey, formatCluster(res.data));
      } catch (err) {
        // A failed refresh still says so, as before: a dissolved cluster must not keep
        // painting its last known state.
        if (isTenantScopeForbidden(err)) showTenantRefusal(requestKey);
        else if (clusterKeyRef.current === requestKey) setClusterError('Failed to load cluster details');
      } finally {
        if (clusterKeyRef.current === requestKey) setClusterLoading(false);
        if (inFlightClusterKeyRef.current === requestKey) inFlightClusterKeyRef.current = null;
      }
    };
    fetchCluster();
  }, [clusterId, tenantId, clusterCacheKey, showFetchedCluster, showTenantRefusal]);

  // BGP config: requested each time the BGP tab opens, as before, with the cached one on
  // screen meanwhile. undefined = not fetched yet, null = fetched and no config,
  // object = fetched and has config.
  const { data: fetchedBgpConfig, isLoading: bgpLoading } = useCachedFetch<ClusterBgpConfig | null>(
    activeTab === 'BGP' ? bgpConfigCacheKey : null,
    async () => {
      const token = await getAccessTokenSilently();
      try {
        const configRes = await getBgpConfig(token, Number(clusterId), tenantId);
        return configRes.data ?? null;
      } catch (err) {
        // Check if it's a 404 error (no initial config) - don't set error in this case
        if (err instanceof AxiosError && err.response?.status === 404) return null;
        console.error('Failed to load BGP configuration:', err);
        throw err;
      }
    }
  );
  // Cloud Propagation reads the config too (instance limits): the last one fetched this
  // session, even while the BGP tab is closed.
  const bgpConfig = fetchedBgpConfig !== undefined ? fetchedBgpConfig : readCachedFetch<ClusterBgpConfig | null>(bgpConfigCacheKey);

  // Clear routerId2 when clusterId changes (switching between clusters)
  useEffect(() => {
    setRouterId2('');
  }, [clusterId]);

  const handleLegacyTagUpdate = useCallback(async (tags: string[]) => {
    if (!clusterId || !tenantId) return;
    const token = await getAccessTokenSilently();
    await updateClusterTags(token, Number(clusterId), { tags }, tenantId);
    await refreshClusterDetails();
  }, [clusterId, tenantId, getAccessTokenSilently, refreshClusterDetails]);

  useEffect(() => {
    if (cluster) {
      setFieldValues({
        Name: cluster.name || '',
        Description: cluster.description || '',
      });
    }
  }, [cluster]);

  /**
   * `revalidate` (arrival, the tab opening) keeps cached rules on screen while it refreshes
   * them; `refresh` (after a rule was saved or deleted) shows the skeleton until the new
   * rules are in, so the old rows are never read as the result.
   */
  const loadClusterPropagationRules = React.useCallback(async (mode: 'revalidate' | 'refresh') => {
    const key = cloudPropagationCacheKey;
    if (!key || !tenantId) return;
    if (mode === 'refresh' || readCachedFetch(key) === undefined) setCloudPropagationLoading(true);
    setCloudPropagationError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await getCloudPropagationRulesByCluster(token, Number(clusterId), tenantId);
      writeCachedFetch(key, res.data);
      if (cloudPropagationKeyRef.current === key) setCloudPropagationRules(res.data);
    } catch (err) {
      if (isTenantScopeForbidden(err)) {
        dropCachedFetch(key);
        if (cloudPropagationKeyRef.current === key) {
          setCloudPropagationRules([]);
          setCloudPropagationError(TENANT_OUT_OF_SCOPE_MESSAGE);
        }
        return;
      }
      if (cloudPropagationKeyRef.current === key) setCloudPropagationError('Failed to load Cloud Propagation rules');
    } finally {
      if (cloudPropagationKeyRef.current === key) setCloudPropagationLoading(false);
    }
  }, [cloudPropagationCacheKey, clusterId, getAccessTokenSilently, tenantId]);

  const refreshClusterPropagationRules = useCallback(
    () => loadClusterPropagationRules('refresh'),
    [loadClusterPropagationRules]
  );

  // Fetch cloud propagation rules when clusterId changes
  useEffect(() => {
    if (clusterId) {
      loadClusterPropagationRules('revalidate');
    }
  }, [clusterId, loadClusterPropagationRules]);

  // Refresh cloud propagation rules when switching to Cloud Propagation tab
  useEffect(() => {
    if (activeTab === 'Cloud Propagation' && clusterId) {
      loadClusterPropagationRules('revalidate');
    }
  }, [activeTab, clusterId, loadClusterPropagationRules]);

  // Save handler for editable fields
  const handleSaveField = async (field: string) => {
    if (!clusterId) return;
    try {
      if (!tenantId) return;
      const token = await getAccessTokenSilently();
      if (field === 'Name') {
        await updateClusterName(token, Number(clusterId), { name: fieldValues['Name'] }, tenantId);
        if (cluster) showFetchedCluster(clusterCacheKey, { ...cluster, name: fieldValues['Name'] });
      }
      setIsEditing(prev => ({ ...prev, [field]: false }));
    } catch (error) {
      setIsEditing(prev => ({ ...prev, [field]: false }));
    }
  };

  // Render function for each item
  const renderDetailItem = (item: ClusterDetailItem) => {
    // Tag field — uses Tags Manager EditableTagList
    if (item.label === 'Tag') {
      // After KD-563-2 (TAGS-API-CONTRACT.md §5.5) `getClusterById` ships
      // rich `tags` (objects) and renames the legacy strings to `tagsLegacy`.
      // Prefer the new field; fall back to `tags`/`tag` for pre-v2
      // environments. Filter non-strings so a rich array silently degrades
      // to an empty legacy list instead of rendering `[object Object]`.
      const legacyTags = (() => {
        const source = cluster?.tagsLegacy ?? cluster?.tags;
        if (Array.isArray(source)) {
          return source.filter(
            (t: any): t is string => typeof t === 'string' && t.trim() !== ''
          );
        }
        if (typeof cluster?.tag === 'string' && cluster.tag && cluster.tag !== '-') return [cluster.tag];
        return [];
      })();
      // §5.5 seed: skip the §5.1 round-trip when the response ships rich tags.
      // `apiTagsToSummaries` returns [] for non-conforming shapes so the hook
      // falls back to its own fetch in pre-v2 environments.
      const initialAssignedTags = apiTagsToSummaries(cluster?.tags);
      return (
        <div className="flex justify-between w-full py-1 items-center min-h-[40px]" key={item.label}>
          <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
          <div className="flex items-center gap-2 w-1/2 px-4 justify-end">
            <EditableTagList
              tags={legacyTags}
              onUpdate={handleLegacyTagUpdate}
              resourceType="rv2-cluster"
              resourceId={clusterId ? Number(clusterId) : null}
              mount="reflector-v2"
              tenantId={tenantId}
              initialAssignedTags={initialAssignedTags}
            />
          </div>
        </div>
      );
    }
    // Editable fields
    if (item.type === 'editable') {
      const isFieldEditing = isEditing[item.label];
      return (
        <div className="flex justify-between w-full py-1 items-center min-h-[40px]" key={item.label}>
          <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
          <div className="flex items-center space-x-2 w-1/2 px-4 justify-end">
            {isFieldEditing ? (
              <>
                <GenericButton
                  onClick={() => handleSaveField(item.label)}
                  icon={<IconCheck className="w-5 h-5 text-semantic-text-secondary" />}
                  isIconOnly
                  variant="secondary"
                  shape="ghost"
                  size="sm"
                  aria-label={`Save ${item.label}`}
                />
                <GenericButton
                  onClick={() => setIsEditing((prev) => ({ ...prev, [item.label]: false }))}
                  icon={<IconCancel className="w-5 h-5 text-semantic-text-secondary" />}
                  isIconOnly
                  variant="secondary"
                  shape="ghost"
                  size="sm"
                  aria-label={`Cancel Edit ${item.label}`}
                />
                <input
                  type="text"
                  value={fieldValues[item.label]}
                  onChange={e => setFieldValues((prev) => ({ ...prev, [item.label]: e.target.value }))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
                />
              </>
            ) : (
              <>
                <GenericButton
                  onClick={() => setIsEditing((prev) => ({ ...prev, [item.label]: true }))}
                  icon={<IconEdit className="w-4 h-4" />}
                  isIconOnly
                  variant="secondary"
                  shape="ghost"
                  size="sm"
                  aria-label={`Edit ${item.label}`}
                />
                <span className="text-sm text-semantic-text-secondary">{fieldValues[item.label]}</span>
              </>
            )}
          </div>
        </div>
      );
    }
    // Tag fields
    if (item.type === 'tag') {
      return (
        <div className="flex justify-between w-full py-1 items-center min-h-[40px]" key={item.label}>
          <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
          <div className="flex items-center w-1/2 px-4 justify-end">
            <GenericTag label={String(item.value)} variant={item.variant as any} />
          </div>
        </div>
      );
    }
    // IAM Permissions
    if (item.type === 'iam') {
      const { label: iamTagLabel, variant: iamTagVariant } = getIamTagState(iamData);
      return (
        <div className="flex justify-between w-full py-1 items-center min-h-[40px]" key={item.label}>
          <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
          <div className="flex items-center w-1/2 px-4 justify-end gap-2">
            <span className="cursor-pointer hover:opacity-80" onClick={() => setIsIamModalOpen(true)}>
              <GenericTag label={iamTagLabel} variant={iamTagVariant as any} />
            </span>
            {/* <GenericButton
              icon={<IconRefresh className="w-4 h-4" />}
              label="Sync"
              variant="refresh"
              shape="ghost"
              size="sm"
              aria-label="Refresh IAM Permissions"
              onClick={reloadIamPermissions}
            /> */}
            {isIamModalOpen && (
              <ClusterIAMPermissionsModal
                key="cluster-iam-modal"
                isOpen={isIamModalOpen}
                onClose={() => setIsIamModalOpen(false)}
                iamData={iamData}
                loading={iamPermissionsLoading}
                error={iamPermissionsError}
              />
            )}
          </div>
        </div>
      );
    }
    // Default (plain text)
    return (
      <div className="flex justify-between w-full py-1 items-center min-h-[40px]" key={item.label}>
        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
        <div className="flex items-center w-1/2 px-4 justify-end">
          {/* Use CopyableValue for ID fields */}
          {(item.label === 'ID' || item.label === 'VPC/VNET/VCN') ? (
            <CopyableValue value={String(item.value)} />
          ) : (
            <span className="text-sm text-semantic-text-secondary">{item.value}</span>
          )}
        </div>
      </div>
    );
  };

  // Use the extracted function to get the details section
  const detailsSection = cluster ? getClusterDetailsSection(cluster) : [];

  // Split items for two columns (using real data)
  const mid = detailsSection[0] ? Math.ceil(detailsSection[0].items.length / 2) : 0;
  const leftColumnItems = detailsSection[0] ? detailsSection[0].items.slice(0, mid) : [];
  const rightColumnItems = detailsSection[0] ? detailsSection[0].items.slice(mid) : [];

  // Refresh function for details and IAM permissions (single loading state = skeletons only, no double loader)
  const handleRefreshDetails = async () => {
    if (!clusterId) return;

    setClusterLoading(true);
    setClusterError(null);
    try {
      await refreshClusterDetails(true);
      reloadIamPermissions();
    } catch (error) {
      console.error('Error refreshing details:', error);
    } finally {
      setClusterLoading(false);
    }
  };

  // Handle dissolve cluster action
  const handleDissolveCluster = async () => {
    if (!clusterId) return;
    
    setDissolveLoading(true);
    setShowErrorAlert(false);
    setShowSuccessAlert(false);
    
    try {
      if (!tenantId) return;
      const token = await getAccessTokenSilently();
      const deleteNeighbors = dissolveOption === 'delete';
      await dissolveCluster(token, Number(clusterId), tenantId, deleteNeighbors);
      // The list we navigate back to must not paint the dissolved cluster from its cache.
      const listKey = clusterListCacheKey(tenantId);
      const cachedList = readCachedFetch<Array<{ id: string | number }>>(listKey);
      if (cachedList) {
        writeCachedFetch(listKey, cachedList.filter((c) => String(c.id) !== String(clusterId)));
      }
      
      // Show success alert
      setShowSuccessAlert(true);
      
      // Close modal after a short delay
      setTimeout(() => {
        setIsDissolveModalOpen(false);
        setDissolveOption('keep');
        // Navigate back to clusters list
        navigate('/reflector-v2/management/clusters');
      }, 2000);
      
    } catch (error) {
      
      // Show error alert
      setErrorMessage(error instanceof Error ? error.message : 'Failed to dissolve cluster');
      setShowErrorAlert(true);
      
      // Keep modal open so user can try again
    } finally {
      setDissolveLoading(false);
    }
  };

  // Remove the complex flattening logic since cluster API should return the same format
  // const flatCloudPropagationRules = ...

  return (
    <div className="relative">
      {clusterError && !cluster && (
        <>
          <div className="mb-4 flex items-center gap-2">
            <GenericButton
              label="Back"
              variant="secondary"
              shape="ghost"
              size="sm"
              iconLeftName="arrowLeft"
              onClick={() => navigate('/reflector-v2/management/clusters')}
            />
          </div>
          <ErrorStateView
            title="Failed to load cluster details"
            message={clusterError}
            onRetry={() => {
              setClusterError(null);
              refreshClusterDetails();
            }}
          />
        </>
      )}
      {(cluster || isClusterDetailsFirstLoad) && (
        <>
          <div className="mb-4 flex items-center gap-2">
            <GenericButton
              label="Back"
              variant="secondary"
              shape="ghost"
              size="sm"
              iconLeftName="arrowLeft"
              onClick={() => navigate('/reflector-v2/management/clusters')}
            />
          </div>
          <div className="flex items-center justify-start mb-2 max-w-[800px] ">
            <GenericTabs
              tabs={TABS}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
          {activeTab === 'Details' && (
            <DataStateView
              loading={clusterLoading || isClusterDetailsFirstLoad}
              error={clusterError}
              isEmpty={!cluster && !clusterLoading}
              onRetry={refreshClusterDetails}
              loadingContent={
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4 auto-rows-auto">
                  <GenericCard isCollapsible={false} headerContent="Details">
                    <div className="md:flex md:gap-8 p-4 space-y-3 md:space-y-0">
                      <div className="flex-1 space-y-3">
                        {[1, 2, 3, 4].map((i) => <SkeletonLine key={i} w="100%" h={24} className="rounded" />)}
                      </div>
                      <div className="flex-1 space-y-3">
                        {[5, 6, 7, 8].map((i) => <SkeletonLine key={i} w="100%" h={24} className="rounded" />)}
                      </div>
                    </div>
                  </GenericCard>
                  <GenericCard isCollapsible={false} headerContent="Reflectors">
                    <div className="p-4">
                      <TableSkeleton columns={4} rows={3} />
                    </div>
                  </GenericCard>
                </div>
              }
              errorTitle="Failed to load cluster details"
              emptyTitle="No cluster details"
              emptyDescription="Cluster details could not be loaded."
            >
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 auto-rows-auto">
              {/* Details Card */}
              <GenericCard isCollapsible={false} headerContent={
                <div className="flex items-center justify-between w-full">
                  <span>Details</span>
                  <div className="flex gap-2">
                    <GenericButton
                      icon={<IconRefresh className="w-4 h-4" />}
                      label="Sync"
                      variant="refresh"
                      shape="ghost"
                      size="sm"
                      aria-label="Refresh Details"
                      onClick={handleRefreshDetails}
                    />
                    <GenericButton
                      icon={<IconTrash className="w-4 h-4" />}
                      label="Dissolve cluster"
                      variant="danger"
                      shape="filled"
                      size="sm"
                      onClick={() => setIsDissolveModalOpen(true)}
                    />
                  </div>
                </div>
              }>
                <div className="md:flex md:gap-8">
                  <div className="flex-1 divide-y divide-gray-100">
                    {leftColumnItems.map(renderDetailItem)}
                  </div>
                  <div className="flex-1 divide-y divide-gray-100 border-t border-gray-100 md:border-t-0">
                    {rightColumnItems.map(renderDetailItem)}
                  </div>
                </div>
              </GenericCard>
              {/* Reflectors Associated */}
              <GenericCard isCollapsible={false} headerContent="Reflectors">
                <div className="flex flex-col gap-6">
                  <ReflectorsAssociated reflectors={cluster?.instances || []} clusterId={clusterId} onRoleChange={refreshClusterDetails} />
                </div>
              </GenericCard>
            </div>
            </DataStateView>
          )}
          {activeTab === 'BGP' && (
            <LicenseGate gate={licenseGate}>
              <BgpDetailsPage
                config={(() => {
                  if (!cluster) return undefined;
                
                  // Always include cluster instance data
                  const baseConfig = {
                    clusterReflectors: cluster?.instances?.map((inst: { id: number }) => inst.id) || [],
                    clusterReflectorNames: cluster?.instances?.map((inst: { name: string }) => inst.name) || [],
                    clusterInterfaceIds: cluster?.instances?.map((inst: { interfaceId: number }) => inst.interfaceId) || [],
                    clusterReflectorRoles: cluster?.instances?.map((inst: { id: number; role: string }) => ({ instanceId: inst.id, role: inst.role })) || [],
                  };
                
                  // If bgpConfig has been fetched (not undefined), use it
                  if (bgpConfig !== undefined) {
                    if (bgpConfig) {
                      // We've fetched BGP config and it exists
                      return {
                        ...bgpConfig,
                        ...baseConfig,
                        localAs: bgpConfig.localAs || '',
                        routerId: bgpConfig.routerId1 || bgpConfig.routerId || '',
                        routerId2: bgpConfig.routerId2 || '',
                      };
                    } else {
                      // bgpConfig is null (fetched but no config exists)
                      return {
                        ...baseConfig,
                        localAs: null,
                        routerId: null,
                        routerId2: null,
                      };
                    }
                  }
                
                  // bgpConfig is undefined (not fetched yet) - pass cluster data but no localAs/routerId
                  // This will make BgpDetailsPage fetch the config
                  return {
                    ...baseConfig,
                    // Don't include localAs/routerId so BgpDetailsPage knows to fetch
                  };
                })()}
                clusterId={clusterId}
                getAccessTokenSilently={getAccessTokenSilently}
                showReflector2
                routerId2={routerId2}
                onRouterId2Change={setRouterId2}
                context="cluster"
                parentLoading={clusterLoading || bgpLoading}
              />
            </LicenseGate>
          )}
          {activeTab === 'Cloud Propagation' && (
            <LicenseGate gate={licenseGate}>
              <CloudPropagationPage
                data={cloudPropagationRules}
                columns={clusterCloudPropagationColumns}
                error={cloudPropagationError}
                loading={cloudPropagationLoading}
                getAccessTokenSilently={getAccessTokenSilently}
                clusterId={clusterId ? Number(clusterId) : undefined}
                onRefresh={refreshClusterPropagationRules}
                instanceLimits={
                  bgpConfig && typeof bgpConfig === "object"
                    ? (bgpConfig as { reflectorInstance?: { instanceLimits?: unknown } }).reflectorInstance
                        ?.instanceLimits
                    : undefined
                }
                disableDynamicTagOptions={isAnyVersionBelow(clusterInstanceVersions)}
                disableNexthopRetry={isAnyVersionBelow(clusterInstanceVersions, NEXTHOP_RETRY_MIN_VERSION)}
                useNexthopVerification={!isAnyVersionBelow(clusterInstanceVersions, NEXTHOP_WATCH_MIN_VERSION)}
              />
            </LicenseGate>
          )}
          {activeTab === 'Cloud Elements' && clusterId && (
            <CloudElementsPage
              columns={clusterCloudElementsColumns as unknown as CloudElementsPageProps['columns']}
              suggestedKeys={[...CLOUD_ELEMENTS_SUGGESTED_KEYS_CLUSTER]}
              clusterId={clusterId}
              getAccessTokenSilently={getAccessTokenSilently}
            />
          )}
          {activeTab === 'Syslog' && cluster?.instances && cluster.instances.length > 0 && (
            <LicenseGate gate={licenseGate}>
              <SyslogPage
                ctxrId={String(cluster.instances[0].id)}
                clusterId={clusterId ? Number(clusterId) : undefined}
                getAccessTokenSilently={getAccessTokenSilently}
              />
            </LicenseGate>
          )}
        </>
      )}
      
      {/* Dissolve Cluster Confirmation Modal */}
      <GenericModal
        isOpen={isDissolveModalOpen}
        onClose={() => {
          setIsDissolveModalOpen(false);
          setDissolveOption('keep'); // Reset radio group when modal is closed
        }}
        title="Dissolve Cluster"
        size="md"
        
      >
         <div className="p-6">
           <div className="mb-6">
             <p className="text-sm text-semantic-text-primary mb-3">
               Are you sure you want to dissolve the cluster <strong>{cluster?.name || clusterId}</strong>?
             </p>
             <div className="bg-semantic-bg-warning-primary border border-semantic-border-warning-primary rounded-lg p-3 mb-4">
               <div className="flex items-start gap-3">
                 <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                   <svg className="w-5 h-5 text-semantic-text-warning" fill="currentColor" viewBox="0 0 20 20">
                     <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                   </svg>
                 </div>
                 <div>
                   <p className="text-sm text-semantic-text-warning">
                     <strong>Warning:</strong> This will permanently remove the cluster and all its associated reflectors.
                   </p>
                   {dissolveOption === 'delete' && (
                     <p className="text-sm text-semantic-text-warning mt-2">
                       All BGP sessions and cloud propagation rules will be permanently deleted along with the cluster.
                     </p>
                   )}
                 </div>
               </div>
             </div>
             
             <div className="mt-4">
               <GenericRadioGroup
                 key={dissolveOption} // Force re-render when state changes
                 options={[
                   {
                     value: 'keep',
                     label: 'Keep BGP configurations and Cloud Propagation'
                   },
                   {
                     value: 'delete',
                     label: 'Delete all BGP configurations and associated Cloud Propagation'
                   }
                 ]}
                 defaultValue={dissolveOption}
                 onChange={(value) => {
                   setDissolveOption(value);
                 }}
               />
             </div>
           </div>
          
          <div className="flex justify-end gap-3">
            <GenericButton
              label="Cancel"
              variant="secondary"
              shape="ghost"
              onClick={() => setIsDissolveModalOpen(false)}
              disabled={dissolveLoading}
            />
            <GenericButton
              label={dissolveLoading ? "Dissolving..." : "Dissolve Cluster"}
              variant="danger"
              shape="filled"
              onClick={handleDissolveCluster}
              loading={dissolveLoading}
              disabled={dissolveLoading}
            />
          </div>
        </div>
      </GenericModal>
      
      {/* Success Alert */}
      <GenericAlert
        isOpen={showSuccessAlert}
        onClose={() => setShowSuccessAlert(false)}
        onConfirm={() => setShowSuccessAlert(false)}
        title="Cluster Dissolved Successfully"
        description={
          <div>
            <p>The cluster has been successfully dissolved.</p>
            {dissolveOption === 'delete' && (
              <p className="mt-2 text-sm text-gray-600">
                BGP configurations and Cloud Propagation rules have also been deleted.
              </p>
            )}
          </div>
        }
        confirmLabel="OK"
        hideCancel={true}
        variant="success"
      />
      
      {/* Error Alert */}
      <GenericAlert
        isOpen={showErrorAlert}
        onClose={() => setShowErrorAlert(false)}
        onConfirm={() => setShowErrorAlert(false)}
        title="Failed to Dissolve Cluster"
        description={
          <div>
            <p>An error occurred while dissolving the cluster:</p>
            <p className="mt-2 text-sm text-red-600 font-mono bg-red-50 p-2 rounded">
              {errorMessage}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Please try again or contact support if the problem persists.
            </p>
          </div>
        }
        confirmLabel="Try Again"
        cancelLabel="Cancel"
        variant="error"
      />
    </div>
  );
}

export default ClusterDetailsPage; 